// app-components/investor-profile/co-investment-network/CoInvestNetworkApp.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../../../config';
import axios from 'axios';
import LoadingSpinner from '../../../../components/ui/LoadingSpinner';
import { Users, Calendar, TrendingUp, DollarSign } from 'lucide-react';
import HoverHint from '../../../../components/ui/hoverHint';
import PartnerSwitcher from './PartnerSwitcher';
import { useMembershipAccess } from '../../../../hooks/membership';

const summaryGridClass = "grid grid-cols-[repeat(auto-fit,minmax(min(100%,11rem),1fr))] gap-3 mb-5";
const summaryCardClass = "min-w-0 bg-gray-50 p-3 rounded-lg text-center";
const summaryLabelClass = "flex flex-wrap items-center justify-center gap-1.5 mb-1 text-center";
const summaryTextClass = "min-w-0 text-xs font-medium leading-snug text-gray-700 break-words";
const hintClass = "w-4 h-4 shrink-0 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help";

const CoInvestNetworkApp = ({ investorId }) => {
    const { t } = useTranslation();
    const { isFree } = useMembershipAccess();
    const [networkData, setNetworkData] = useState(null);
    const [partnerNames, setPartnerNames] = useState({});
    const [dealInfo, setDealInfo] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchNetworkData = async () => {
            if (!investorId) return;

            try {
                setLoading(true);
                setError(null);

                const response = await axios.get(`${API_URL}/investors/${investorId}/coinvest`, {
                    params: { by: 'id' },
                    withCredentials: true,
                });

                const data = response.data;
                setNetworkData(data);

                // Fetch partner details (id + name) for proper link generation
                if (data.edges && data.edges.length > 0) {
                    const partnerDetails = {};
                    const dealDetails = {};

                    // Fetch details for each partner
                    const partnerPromises = data.edges.map(async (edge) => {
                        try {
                            const partnerResponse = await axios.get(`${API_URL}/investors/${edge.pid}`, {
                                params: { by: '_id' },
                                withCredentials: true,
                            });
                            if (partnerResponse.data) {
                                partnerDetails[edge.pid] = {
                                    id: partnerResponse.data.id,
                                    name: partnerResponse.data.name
                                };
                            }
                        } catch (err) {
                            console.warn(`Failed to fetch partner details for ${edge.pid}:`, err);
                        }
                    });

                    await Promise.all(partnerPromises);
                    setPartnerNames(partnerDetails);

                    // Fetch deal information for all deal IDs from recent_ids
                    const allDealIds = new Set();

                    // Collect deal IDs from edges.recent_ids
                    data.edges.forEach(edge => {
                        if (edge.recent_ids && Array.isArray(edge.recent_ids)) {
                            edge.recent_ids.forEach(deal => {
                                if (deal.id) allDealIds.add(deal.id);
                            });
                        }
                    });

                    // Fetch deal information with delay to avoid rate limiting
                    // Limit to first 20 deals to prevent too many API calls
                    const dealIdsArray = Array.from(allDealIds).slice(0, 20);
                    console.log(`[CoInvestNetworkApp] Loading ${dealIdsArray.length} deals out of ${allDealIds.size} total`);
                    for (let i = 0; i < dealIdsArray.length; i++) {
                        const dealId = dealIdsArray[i];
                        const dealData = await fetchDealInfo(dealId);
                        if (dealData) {
                            dealDetails[dealId] = dealData;
                        }
                        // Add small delay between requests to avoid rate limiting
                        if (i < dealIdsArray.length - 1) {
                            await new Promise(resolve => setTimeout(resolve, 100));
                        }
                    }
                    setDealInfo(dealDetails);
                }
            } catch (err) {
                console.error('Error fetching co-investment network:', err);
                setError('Failed to load co-investment network');
            } finally {
                setLoading(false);
            }
        };

        fetchNetworkData();
    }, [investorId]);

    // Fetch deal information
    const fetchDealInfo = async (dealId) => {
        try {
            const response = await axios.get(`${API_URL}/deals/${dealId}/basic`);
            return response.data;
        } catch (err) {
            console.warn(`Failed to fetch deal info for ${dealId}:`, err);
            return null;
        }
    };

    // Format date to "April 3, 2025" format
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="w-full rounded-md bg-white p-6 shadow">
                <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    if (error || !networkData) {
        return null; // Don't show anything on error
    }

    // Don't show if no network data
    if (networkData.visible_count === 0 && networkData.hidden_count === 0) {
        return null;
    }

    const totalPartners = networkData.visible_count + networkData.hidden_count;

    return (
        <div className="w-full rounded-md bg-white p-6 shadow">
            <div className="mb-6">
                <h3 className="flex flex-wrap items-center gap-2 text-2xl text-stone-900 font-semibold leading-tight break-words">
                    <Users className="h-5 w-5 shrink-0" />
                    <span className="min-w-0">{t('pageInvProf.coInvestNetwork')}</span>
                </h3>
            </div>

            {/* Network Summary - Compact */}
            <div className={summaryGridClass}>
                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <Users className="h-4 w-4 shrink-0 text-blue-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.totalPartners')}</span>
                        <HoverHint message={t('pageInvProf.totalPartnersHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                        {networkData.universe?.partners_total || 0}
                    </div>
                </div>

                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <TrendingUp className="h-4 w-4 shrink-0 text-purple-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.keyPartners')}</span>
                        <HoverHint message={t('pageInvProf.keyPartnersHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                        {networkData.universe?.partners_ge2 || 0}
                    </div>
                </div>

                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <Users className="h-4 w-4 shrink-0 text-indigo-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.partnersShown')}</span>
                        <HoverHint message={t('pageInvProf.partnersShownHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                        {networkData.edges?.length || 0} / {networkData.total_core_partners || 0}
                    </div>
                </div>


                <div className={summaryCardClass}>
                    <div className={summaryLabelClass}>
                        <Calendar className="h-4 w-4 shrink-0 text-orange-600" />
                        <span className={summaryTextClass}>{t('pageInvProf.lastUpdated')}</span>
                        <HoverHint message={t('pageInvProf.lastUpdatedHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-xs leading-snug text-gray-600 break-words">
                        {networkData.updated_at ? formatDate(networkData.updated_at) : t('pageInvProf.unknown')}
                    </div>
                </div>
            </div>

            {/* Partner Network Display */}
            {networkData.edges && networkData.edges.length > 0 && (
                <>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">
                        {networkData.edges.length === 1 ? t('pageInvProf.topPartner') : t('pageInvProf.topPartners')}
                    </h4>

                    {/* Always show the switcher */}
                    <PartnerSwitcher
                        partners={networkData.edges}
                        partnerNames={partnerNames}
                        dealInfo={dealInfo}
                        hiddenCount={networkData.hidden_count}
                    />
                </>
            )}
        </div>
    );
};

export default CoInvestNetworkApp;
